import {
	ASSET_TYPE_CONFIGS,
	type AreaIds,
	getAreaAssetIdForAssetType,
	resolveAssetType,
} from "@/pages/workOrders/config/assetFieldConfig";
import { useClipSpecs } from "@/pages/workOrders/components/ClipSpecsPanel";
import { WorkOrderNumberField } from "@/pages/workOrders/components/WorkOrderNumberField";
import { useWorkOrderNumberPreview } from "@/pages/workOrders/hooks/useWorkOrderNumberPreview";
import { invalidateWorkOrderQueries } from "@/pages/workOrders/hooks/invalidateWorkOrderQueries";
import { invalidatePlanQueries } from "@/shared/utils/planCache";
import type { Clip } from "@/shared/models/Clip";
import type { ProductForSelector, WorkOrder } from "@/shared/models/WorkOrder";
import {
	type CalendarEvent,
	type CalendarEventType,
	upsertCalendarEvent,
} from "@/shared/repositories/CalendarEventRepository";
import type { PlanChangeRef } from "@/shared/repositories/PlanChangeRepository";
import { useDelayAssetsGrouped } from "@/shared/selectors/AssetButtonGroup";
import { ErrorModal, getMomentTz, MesfModal } from "@dexteel/mesf-core";
import { Box, Button, Card, CardContent, Grid2 } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { QuestionDialogRaw } from "@/controls/modals/QuestionDialogRaw";
import { EventScheduleFields } from "./EventScheduleFields";
import { BreakPlanConfirmationModal } from "./BreakPlanConfirmationModal";
import { useDeleteCalendarItem } from "../hooks/useDeleteCalendarItem";
import {
	WorkOrderEventFields,
	WorkOrderEventDetails,
} from "./WorkOrderEventFields";

const moment = getMomentTz();

export interface EventForm {
	EventTypeCode: CalendarEventType;
	Start: Date | null;
	End: Date | null;
	AssetId: number | null; // W
	AssetIds: number[]; // M/D/B multiselect
	ProductId: number | null;
	ClipId: number | null;
	Quantity: number | null;
	Notes: string;
	Comments: string;
	NextLocations: string[];
	IsSpell: boolean;
}

type Props = {
	open: boolean;
	onHide: (
		success: boolean,
		refs?: PlanChangeRef[],
		workOrderNumber?: string | null,
	) => void;
	assetId: number | null; // line being viewed — prefills the W line
	defaultStart: Date | null;
	defaultEnd: Date | null;
	areaIds: AreaIds;
	event?: CalendarEvent | null;
	workOrder?: WorkOrder | null;
	/** the viewed line's month events, for collision checks on Start/End */
	lineEvents?: CalendarEvent[];
	workOrdersOnly?: boolean;
};

export const EventEditorModal = ({
	open,
	onHide,
	assetId,
	defaultStart,
	defaultEnd,
	areaIds,
	event = null,
	workOrder = null,
	lineEvents = [],
	workOrdersOnly = false,
}: Props) => {
	const isWoEdit = workOrder != null;
	const isEventEdit = !isWoEdit && event != null;
	const isEdit = isWoEdit || isEventEdit;
	const isWindowLocked = isWoEdit && workOrder.Status !== "Pending";
	const isQuantityLocked = isWoEdit && workOrder.Status === "Loaded";
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [pendingSubmit, setPendingSubmit] = useState<EventForm | null>(null);
	const [pendingBreakSubmit, setPendingBreakSubmit] =
		useState<EventForm | null>(null);
	const [selectedProduct, setSelectedProduct] =
		useState<ProductForSelector | null>(null);
	const [selectedClip, setSelectedClip] = useState<Clip | null>(null);

	const { data: assets } = useDelayAssetsGrouped();

	const initialValues = useMemo<EventForm>(() => {
		const initialAssetId = workOrder
			? workOrder.AssetId
			: event
				? event.AssetId
				: assetId;
		return {
			EventTypeCode: workOrder ? "W" : (event?.EventTypeCode ?? "W"),
			Start: event ? moment.utc(event.Start).toDate() : defaultStart,
			End: event ? moment.utc(event.End).toDate() : defaultEnd,
			AssetId: initialAssetId,
			AssetIds: !workOrder && initialAssetId != null ? [initialAssetId] : [],
			ProductId: workOrder?.ProductId ?? null,
			ClipId: workOrder?.ClipId ?? null,
			Quantity: workOrder?.Quantity ?? null,
			Notes: workOrder?.Notes ?? "",
			Comments: event?.Comments ?? "",
			NextLocations: (workOrder?.NextLocations ?? "")
				.split(",")
				.map((t) => t.trim())
				.filter(Boolean),
			IsSpell: workOrder ? false : (event?.IsSpell ?? false),
		};
	}, [assetId, defaultStart, defaultEnd, event, workOrder]);
	const form = useForm<EventForm>({ defaultValues: initialValues });

	const { control, reset, watch, handleSubmit } = form;

	const eventType = watch("EventTypeCode");
	const formAssetId = watch("AssetId");
	const quantity = watch("Quantity");
	const clipIdValue = watch("ClipId");
	const productIdValue = watch("ProductId");
	const startValue = watch("Start");
	const endValue = watch("End");
	const assetIdsValue = watch("AssetIds");
	const selectedAsset = assets?.find((a) => a.AssetId === formAssetId);
	const numberPreview = useWorkOrderNumberPreview(
		formAssetId,
		open && !isEdit && eventType === "W" && !!selectedAsset,
	);

	const assetType = selectedAsset
		? resolveAssetType(
				selectedAsset.AssetId,
				selectedAsset.ParentAssetId,
				areaIds,
			)
		: "weld";
	const config = ASSET_TYPE_CONFIGS[assetType];
	const isClip = !!config.usesClip;
	const areaAssetId = getAreaAssetIdForAssetType(assetType, areaIds);

	useEffect(() => {
		if (open) {
			reset(initialValues);
			setSelectedProduct(null);
			setSelectedClip(null);
		}
	}, [open, initialValues, reset]);

	// Live collision check against the viewed line's plan. Only that line's
	// events are loaded, so a W pointed at another line skips the check.
	const collision = useMemo(() => {
		const result = {
			blockers: [] as CalendarEvent[],
			pushed: [] as CalendarEvent[],
		};
		// Locked work orders can save metadata without revalidating their existing
		// window, just like the backend. They cannot move or push another order.
		if (!startValue || !endValue || eventType === "B" || isWindowLocked)
			return result;
		const applies =
			eventType === "W"
				? formAssetId != null && formAssetId === assetId
				: eventType === "H" || assetIdsValue.includes(assetId ?? -1);
		if (!applies) return result;
		const s = startValue.getTime();
		const e = endValue.getTime();

		for (const ev of lineEvents) {
			if (workOrder && ev.WorkOrderId === workOrder.WorkOrderId) continue;
			if (event && ev.EventId != null && ev.EventId === event.EventId) continue;
			if (
				s >= moment.utc(ev.End).valueOf() ||
				e <= moment.utc(ev.Start).valueOf()
			)
				continue;
			if (ev.EventTypeCode === "W" && ev.Status !== "Cancelled") {
				// A legacy Pending occupies its displayed shift but cannot be pushed
				// by the event-based cascade. It must first be scheduled explicitly.
				if (ev.Status !== "Pending" || ev.EventId == null)
					result.blockers.push(ev);
				else if (eventType !== "H") result.pushed.push(ev);
			} else if (
				eventType === "W" &&
				!isWindowLocked &&
				["M", "D", "H"].includes(ev.EventTypeCode)
			) {
				result.blockers.push(ev);
			}
		}
		return result;
	}, [
		startValue,
		endValue,
		eventType,
		formAssetId,
		assetIdsValue,
		assetId,
		lineEvents,
		workOrder,
		event,
		isWindowLocked,
	]);

	const blockerText = collision.blockers.length
		? `Overlaps ${collision.blockers
				.map((b) =>
					b.EventTypeCode === "W"
						? `${b.WorkOrderNumber ?? "—"} · ${b.PartNumber ?? "?"} (${b.Status}${b.EventId == null ? ", no calendar event" : ""})`
						: { M: "maintenance", D: "downtime", H: "holiday", B: "break" }[
								b.EventTypeCode
							],
				)
				.join(", ")} — that window is locked`
		: null;

	const finishSave = async (
		refs: PlanChangeRef[] = [],
		workOrderNumber?: string | null,
	) => {
		if (eventType === "W") {
			await invalidateWorkOrderQueries(queryClient);
		} else {
			await invalidatePlanQueries(queryClient, {
				assetIds:
					eventType === "H"
						? undefined
						: [assetId, ...assetIdsValue, ...refs.map((r) => r.assetId)],
				workOrders: true,
				planChange: true,
			});
		}
		onHide(true, refs, workOrderNumber);
	};

	const mutation = useMutation({
		mutationFn: upsertCalendarEvent,
		onSuccess: (result) =>
			finishSave(result.planChanges, result.workOrderNumber),
		onError: (e: Error) => setError(e.message),
	});

	const deleter = useDeleteCalendarItem((refs) => onHide(true, refs), setError);

	const submitEvent = (data: EventForm) => {
		if (!data.Start || !data.End) return;

		const isWorkOrder = workOrder != null || data.EventTypeCode === "W";
		const line = workOrder?.AssetId ?? data.AssetId;
		mutation.mutate({
			eventId: event?.EventId ?? null,
			workOrderId: workOrder?.WorkOrderId ?? null,
			eventTypeCode: workOrder ? "W" : data.EventTypeCode,
			assetIds: isWorkOrder
				? line
					? [line]
					: []
				: data.EventTypeCode === "H"
					? null
					: data.AssetIds,
			start: data.Start.toISOString(),
			end: data.End.toISOString(),
			comments: workOrder ? (event?.Comments ?? null) : data.Comments || null,
			productId: isWorkOrder && !workOrder ? data.ProductId : null,
			clipId: isWorkOrder && !workOrder ? data.ClipId : null,
			quantity: isWorkOrder ? data.Quantity : null,
			notes: isWorkOrder ? data.Notes || null : null,
			nextLocations: isWorkOrder ? data.NextLocations.join(", ") || null : null,
			// Ship is a Next Location now; '' keeps whatever the old column holds
			ship: isWorkOrder ? "" : null,
			isSpell: data.EventTypeCode === "B" ? data.IsSpell : null,
		});
	};

	const onSubmit = (data: EventForm) => {
		if (!data.Start || !data.End) return;
		if (data.EventTypeCode === "B") {
			setPendingBreakSubmit(data);
			return;
		}
		if (collision.blockers.length) return;
		if (collision.pushed.length) {
			setPendingSubmit(data);
			return;
		}
		submitEvent(data);
	};

	const productData =
		selectedProduct ??
		(isWoEdit && !isClip ? (workOrder as unknown as ProductForSelector) : null);

	const clipSpecs = useClipSpecs({
		selectedClip,
		workOrderRow: isWoEdit ? workOrder : null,
	});

	const extendedPartNumber = isWoEdit
		? isClip
			? (workOrder?.ClipPartNumber ?? "")
			: (workOrder?.ExtendedPartNumber ?? "")
		: isClip
			? (selectedClip?.ClipPartNumber ?? "")
			: (selectedProduct?.ExtendedPartNumber ?? "");

	const title = isWoEdit
		? "EDIT WORK ORDER"
		: isEventEdit
			? "EDIT EVENT"
			: eventType === "W"
				? "NEW WORK ORDER"
				: "NEW EVENT";

	const requiredComplete =
		formAssetId != null &&
		(isClip ? clipIdValue != null : productIdValue != null) &&
		(quantity ?? 0) > 0;
	const showDetails = isEdit || eventType !== "W" || requiredComplete;

	const canDelete = (isWoEdit && workOrder.Status === "Pending") || isEventEdit;

	return (
		<>
			<MesfModal
				id="modal-new-calendar-event"
				title={title}
				open={open}
				handleClose={() => onHide(false)}
				maxWidth="md"
				fullWidth
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						sx={{ backgroundColor: "#f6f6f6", overflow: "auto" }}
					>
						<Card sx={{ mb: 2 }}>
							<CardContent>
								<Grid2 container spacing={2}>
									{eventType === "W" && (
										<Grid2 size={12}>
											<WorkOrderNumberField
												value={
													isEdit
														? (workOrder?.WorkOrderNumber ??
															event?.WorkOrderNumber)
														: numberPreview.data?.WorkOrderNumber
												}
												loading={!isEdit && numberPreview.isFetching}
											/>
										</Grid2>
									)}
									<EventScheduleFields
										form={form}
										assets={assets ?? []}
										isEdit={isEdit}
										isWindowLocked={isWindowLocked}
										workOrdersOnly={workOrdersOnly}
										blockerText={blockerText}
									/>
									{eventType === "W" && (
										<WorkOrderEventFields
											control={control}
											assets={assets ?? []}
											isWoEdit={isWoEdit}
											isQuantityLocked={isQuantityLocked}
											isClip={isClip}
											areaAssetId={areaAssetId}
											extendedPartNumber={extendedPartNumber}
											setSelectedProduct={setSelectedProduct}
											setSelectedClip={setSelectedClip}
										/>
									)}
								</Grid2>
							</CardContent>
						</Card>

						{eventType === "W" && (
							<WorkOrderEventDetails
								control={control}
								config={config}
								showDetails={showDetails}
								isClip={isClip}
								clipSpecs={clipSpecs}
								clipIdValue={clipIdValue}
								productData={productData}
								assetType={assetType}
								quantity={quantity}
							/>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "16px 24px" }}>
						<Box
							sx={{
								display: "flex",
								width: "100%",
								gap: 2,
								alignItems: "center",
							}}
						>
							{canDelete && (
								<Button
									variant="outlined"
									color="error"
									disabled={deleter.deleting}
									onClick={() =>
										// WO grid: event may not be loaded
										deleter.requestDelete(
											event ?? {
												EventId: null,
												EventTypeCode: "W",
												AssetId: workOrder?.AssetId ?? null,
												WorkOrderId: workOrder?.WorkOrderId ?? null,
												WorkOrderNumber: workOrder?.WorkOrderNumber ?? null,
											},
										)
									}
								>
									Delete
								</Button>
							)}
							<Box sx={{ flex: 1 }}>
								<FooterModalButtons
									isSubmitLoading={mutation.isLoading}
									disabled={!!blockerText}
									onHide={() => onHide(false)}
									buttonLabel={isEdit ? "Save" : "Create"}
								/>
							</Box>
						</Box>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			{deleter.dialogs}
			<QuestionDialogRaw
				title="Push scheduled work orders"
				message={`Saving this window will push ${
					collision.pushed.length
				} pending work order(s) later: ${collision.pushed
					.map((p) => p.WorkOrderNumber ?? "—")
					.join(", ")}. Continue?`}
				open={pendingSubmit != null}
				onClose={(answer) => {
					const data = pendingSubmit;
					setPendingSubmit(null);
					if (answer === "YES" && data) submitEvent(data);
				}}
			/>
			{pendingBreakSubmit?.Start && pendingBreakSubmit.End && (
				<BreakPlanConfirmationModal
					intent={event ? "edit" : "create"}
					request={{
						eventId: event?.EventId ?? null,
						assetIds: pendingBreakSubmit.AssetIds,
						start: pendingBreakSubmit.Start.toISOString(),
						end: pendingBreakSubmit.End.toISOString(),
						comments: pendingBreakSubmit.Comments || null,
						isSpell: pendingBreakSubmit.IsSpell,
					}}
					onSaved={finishSave}
					onCancel={() => setPendingBreakSubmit(null)}
				/>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
